import { useState } from 'react'
import {
  useStripe,
  useElements,
  CardElement
} from '@stripe/react-stripe-js'
import { Button } from '@/components/ui/button'
import { AlertCircle, CreditCard, Lock } from 'lucide-react'
import { stripeService } from '@/services/stripeService'
import { useTranslation } from 'react-i18next'
import { useCurrency } from '@/hooks/useCurrency'

interface StripePaymentFormProps {
  amount: number
  enableAutoTopup?: boolean
  autoTopupThreshold?: number
  autoTopupAmount?: number
  onPaymentSuccess: (paymentIntentId: string) => void
  onPaymentError: (error: string) => void
  isProcessing: boolean
  setIsProcessing: (processing: boolean) => void
}

/**
 * Optimal Payment Intent implementation for custom amounts
 * This shows the recommended flow for production use
 */
export default function StripePaymentForm({
  amount,
  enableAutoTopup = false,
  autoTopupThreshold = 5,
  autoTopupAmount = 25,
  onPaymentSuccess,
  onPaymentError,
  isProcessing,
  setIsProcessing
}: StripePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation('app')
  const { currency } = useCurrency()
  const [cardError, setCardError] = useState<string>('')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)
    setCardError('')

    try {
      const cardElement = elements.getElement(CardElement)

      if (!cardElement) {
        throw new Error(t('payment.errors.cardElement'))
      }

      // Step 1: Create Payment Intent on your backend
      console.log('Creating payment intent for amount:', amount, 'enableAutoTopup:', enableAutoTopup, 'threshold:', autoTopupThreshold, 'autoAmount:', autoTopupAmount)
      const { clientSecret } = await stripeService.createPaymentIntent(
        amount, 
        enableAutoTopup, 
        autoTopupThreshold, 
        autoTopupAmount,
        currency.code
      )

      // Step 2: Confirm payment with Stripe
      console.log('Confirming payment with client secret')
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            // TODO: You can add customer info here if available
          },
        },
      })

      if (error) {
        throw new Error(error.message || t('payment.errors.confirmationFailed'))
      }

      if (paymentIntent?.status === 'succeeded') {
        onPaymentSuccess(paymentIntent.id)
      } else {
        throw new Error(t('payment.errors.statusFailed', { status: paymentIntent?.status || 'unknown' }))
      }

    } catch (error) {
      const fallbackMessage = t('payment.errors.generic')
      const errorMessage = error instanceof Error ? error.message : fallbackMessage
      onPaymentError(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
    }).format(value)
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Card Payment Form */}
        <div className="space-y-4">
          <div className="p-4 border rounded-lg bg-muted/20">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t('payment.cardInformation')}</span>
            </div>

            <div className="p-3 border rounded-md bg-background">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#374151',
                      '::placeholder': {
                        color: '#9CA3AF',
                      },
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                    },
                    invalid: {
                      color: '#EF4444',
                    },
                  },
                }}
                onChange={(event) => {
                  if (event.error) {
                    setCardError(event.error.message)
                  } else {
                    setCardError('')
                  }
                }}
              />
            </div>
          </div>

          {/* Security notice */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>{t('payment.secureNotice')}</span>
          </div>

          {/* Card Error */}
          {cardError && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{cardError}</p>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {t('payment.processing')}
            </span>
          ) : (
            t('payment.payAmount', { amount: formatCurrency(amount) })
          )}
        </Button>
      </form>
    </div>
  )
}
